import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SidebarLayout from "./SidebarLayout";
import BusinessSelector from "../../components/BusinessSelector";
import ShopVisibilityStatus from "../../components/ShopVisibilityStatus";
import AddressAutocompleteInput from "../../components/AddressAutocompleteInput";
import {
  getMyBusinesses,
  createLocation,
} from "../../services/businessService";
import axios from "axios";
import "./shopStyles.css";
import "./LocationManagement.css";
import { TIMEZONE_OPTIONS } from "../../constants/timezones";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;

const Locations = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]); // Phase 3: User's businesses
  const [selectedBusinessId, setSelectedBusinessId] = useState(null); // Phase 3: Current business
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSuccess, setAddSuccess] = useState(false);
  const [error, setError] = useState("");
  const [businessTypes, setBusinessTypes] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showProrationModal, setShowProrationModal] = useState(false);
  const [prorationData, setProrationData] = useState(null);
  const [newLocationData, setNewLocationData] = useState(null);
  const [prorationLoading, setProrationLoading] = useState(false);
  const [showAddToSubscriptionModal, setShowAddToSubscriptionModal] =
    useState(false);
  const [showPaymentConsentModal, setShowPaymentConsentModal] = useState(false);
  const [pendingLocationId, setPendingLocationId] = useState(null);
  const [paymentConsentData, setPaymentConsentData] = useState(null);
  const [showLocationCreatedModal, setShowLocationCreatedModal] =
    useState(false);
  const [locationCreatedData, setLocationCreatedData] = useState(null);
  const [showLocationAddedModal, setShowLocationAddedModal] = useState(false);
  const [locationAddedData, setLocationAddedData] = useState(null);
  const [formData, setFormData] = useState({
    type: [],
    locationMode: "hybrid",
    street: "",
    city: "",
    state: "",
    zipCode: "",
    phone: "",
    email: "",
    timezone: "",
    lat: "",
    lng: "",
  });

  const safeBusinesses = Array.isArray(businesses) ? businesses : [];

  const decodeTokenPayload = (token) => {
    try {
      const [, payloadPart] = token.split(".");
      if (!payloadPart) return null;

      const base64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
      const paddedBase64 = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
      const decoded =
        typeof atob === "function"
          ? atob(paddedBase64)
          : Buffer.from(paddedBase64, "base64").toString("utf-8");

      return JSON.parse(decoded);
    } catch (error) {
      return null;
    }
  };

  const getCurrentRoleCode = () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) return null;

    const payload = decodeTokenPayload(token);
    if (!payload) return null;

    if (payload.preferredRole) return payload.preferredRole;
    if (payload.role) return payload.role;
    if (Array.isArray(payload.roles) && payload.roles.length > 0) {
      return payload.roles[0];
    }

    return null;
  };

  const isOwnerRole = getCurrentRoleCode() === "R1";

  // The location's timezone is deliberately NOT detected or pre-filled.
  //
  // Pre-selecting a value - even a correct detection - lets the owner submit
  // without ever making the choice, and hides a WRONG detection (travel, VPN,
  // mismatched locale) behind a value that looks plausible. It must be actively
  // chosen (policy 2026-10-03).

  useEffect(() => {
    fetchBusinesses(); // Phase 3: Fetch businesses first
    fetchLocations();
    fetchBusinessTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase 3: Fetch user's businesses
  const fetchBusinesses = async () => {
    try {
      const businessesData = await getMyBusinesses();
      const normalizedBusinesses = Array.isArray(businessesData)
        ? businessesData
        : Array.isArray(businessesData?.businesses)
          ? businessesData.businesses
          : [];
      setBusinesses(normalizedBusinesses);

      // Set initial selected business
      if (normalizedBusinesses.length > 0 && !selectedBusinessId) {
        setSelectedBusinessId(normalizedBusinesses[0]._id);
      }
    } catch (error) {
      setBusinesses([]);
    }
  };

  // Get current business name for location naming
  const getCurrentBusinessName = () => {
    const currentBusiness = safeBusinesses.find(
      (b) => b._id === selectedBusinessId,
    );
    return currentBusiness?.businessName || "";
  };

  const fetchLocations = async () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/api/shop/my-shops`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLocations(
        Array.isArray(response.data)
          ? response.data
          : response.data.shops || [],
      );
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessTypes = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/business-categories`,
      );
      setBusinessTypes(res.data.types || []);
    } catch (error) {
      // Fallback to default types
      setBusinessTypes([
        { value: "hair_salon", label: "Hair Salon", emoji: "💇" },
        { value: "barber_shop", label: "Barber Shop", emoji: "💈" },
        { value: "nail_salon", label: "Nail Salon", emoji: "💅" },
        { value: "spa", label: "Spa & Wellness", emoji: "🧖" },
        { value: "beauty_salon", label: "Beauty Salon", emoji: "💄" },
        { value: "med_spa", label: "Medical Spa", emoji: "⚕️" },
      ]);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleStreetChange = (street) => {
    setFormData((prev) => ({
      ...prev,
      street,
      lat: "",
      lng: "",
    }));
  };

  const handleAddressSelect = (address) => {
    setFormData((prev) => ({
      ...prev,
      street: address.street || prev.street,
      city: address.city || prev.city,
      state: address.state || prev.state,
      zipCode: address.zip || prev.zipCode,
      lat: address.lat ?? "",
      lng: address.lng ?? "",
    }));
  };

  const handleTypeChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      type: checked
        ? [...prev.type, value]
        : prev.type.filter((t) => t !== value),
    }));
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();

    // Never submit without an actively chosen timezone (browser guard, not backend).
    if (!formData.timezone || !formData.timezone.trim()) {
      setError("Please choose your timezone.");
      return;
    }

    setError("");

    // Phase 3: Ensure business is selected
    if (!selectedBusinessId) {
      setError("Please select a business first.");
      return;
    }

    if (!formData.lat || !formData.lng) {
      setError(
        "Coordinates (lat/lng) are required. Please select a valid address from the suggestions.",
      );
      return;
    }

    const allowedModes = [
      "commission_only",
      "booth_rental_only",
      "hybrid",
    ];
    if (!allowedModes.includes(formData.locationMode)) {
      setError(
        "Please select a valid location mode: Commission Only, Hybrid, or Booth Rental Only.",
      );
      return;
    }

    const token = localStorage.getItem("ogestyle_token");

    try {
      // Step 1: Check subscription status first
      const subResponse = await axios.get(
        `${API_BASE_URL}/api/subscription-new/status`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const { trialStatus, hasSubscription, status } = subResponse.data;
      const normalizedTrialStatus = (trialStatus || "").toLowerCase();
      const hasActiveSub = hasSubscription === true && status === "active";
      const isTrialExpired = normalizedTrialStatus === "expired";

      if (isTrialExpired && !hasActiveSub) {
        // Trial expired and no active subscription - redirect to subscribe
        setError(
          "Your free trial has ended. Please subscribe to add more locations.",
        );
        setShowAddModal(false);
        setTimeout(() => {
          navigate(
            "/dashboard/settings/subscription/checkout?reason=add_location",
          );
        }, 2000);
        return;
      }

      // Step 2: Get current user profile to get owner email
      const userResponse = await axios.get(
        `${API_BASE_URL}/api/users/profile`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Get selected business details
      const selectedBusiness = safeBusinesses.find(
        (b) => b._id === selectedBusinessId,
      );
      if (!selectedBusiness) {
        setError("Selected business not found.");
        return;
      }

      // Get owner email from user profile
      const ownerEmail = userResponse.data?.email || "";

      if (!ownerEmail) {
        setError(
          "Could not determine owner email. Please ensure you are logged in.",
        );
        return;
      }
      // Get business email to use as default if location email not provided
      const businessEmail = selectedBusiness.businessEmail || ownerEmail;
      const locationEmail = formData.email.trim() || businessEmail;

      // Step 3: Create location first (but not yet in subscription)
      const data = {
        ownerEmail,
        businessId: selectedBusinessId,
        businessName: selectedBusiness.name,
        shopName: selectedBusiness.name,
        type: formData.type,
        locationMode: formData.locationMode,
        street: formData.street,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        phone: formData.phone,
        email: locationEmail,
        timezone: formData.timezone,
        lat: formData.lat,
        lng: formData.lng,
      };
      const newLocation = await createLocation(data);

      if (!hasActiveSub) {
        // No active subscription path (including trial/onboarding): create location without immediate billing.
        setShowAddModal(false);
        setAddSuccess(true);
        fetchLocations();
        return;
      }

      // Step 4: Get proration preview (active subscription path)
      const prorationPreview = await axios.post(
        `${API_BASE_URL}/api/subscription-new/preview-add-location`,
        { locationId: newLocation._id },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Step 5: Show proration confirmation modal
      setNewLocationData(newLocation);
      setProrationData(prorationPreview.data);
      setShowAddModal(false);
      setShowProrationModal(true);
    } catch (err) {
      setError(err.response?.data?.message || "Error adding location");
    }
  };

  const handleConfirmProration = async () => {
    setProrationLoading(true);
    const token = localStorage.getItem("ogestyle_token");

    try {
      // Add location to subscription (charges immediately)
      const response = await axios.post(
        `${API_BASE_URL}/api/subscription-new/add-location`,
        { locationId: newLocationData._id },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        setShowProrationModal(false);
        setAddSuccess(true);
        setProrationData(null);
        setNewLocationData(null);
        fetchLocations();
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to add location to subscription",
      );
      setShowProrationModal(false);
    } finally {
      setProrationLoading(false);
    }
  };

  const handleCancelProration = async () => {
    setShowProrationModal(false);

    // Important: Location was created but NOT added to subscription
    // It will remain in 'pending' status and will NOT be visible to the public
    // The location will NOT be charged and will NOT appear in customer search
    // User can confirm and pay later, or delete the location

    if (newLocationData) {
      setLocationCreatedData({
        name: newLocationData.name,
      });
      setShowLocationCreatedModal(true);
    }

    setProrationData(null);
    setNewLocationData(null);
    fetchLocations();
  };

  const handleToggleVisibility = async (locationId, currentVisibility) => {
    const token = localStorage.getItem("ogestyle_token");
    const newVisibility = !currentVisibility;

    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/shop/${locationId}/toggle-visibility`,
        { isPubliclyVisible: newVisibility },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.message) {
        alert(response.data.message);
      }

      // Refresh locations to show updated status
      fetchLocations();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to toggle visibility");
    }
  };

  const handleAddLocationToSubscription = async (
    locationId,
    confirmSetDefault = false,
  ) => {
    const token = localStorage.getItem("ogestyle_token");

    // First confirmation (show modal instead of browser confirm)
    if (!confirmSetDefault) {
      setPendingLocationId(locationId);
      setShowAddToSubscriptionModal(true);
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/subscription-new/add-location`,
        { locationId, confirmSetDefault },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success) {
        const invoice = response.data.invoice;
        const prorationAmount = invoice?.prorationAmount;

        setLocationAddedData({
          prorationAmount,
        });
        setShowLocationAddedModal(true);

        // Close any open modals
        setShowAddToSubscriptionModal(false);
        setShowPaymentConsentModal(false);
        setPendingLocationId(null);
        setPaymentConsentData(null);

        // Refresh locations to show updated status
        fetchLocations();
      } else if (response.data.requiresConsent) {
        // Backend detected no default payment method - show consent modal
        setPaymentConsentData({
          locationId,
          paymentMethod: response.data.paymentMethod,
          prorationAmount: response.data.prorationAmount,
          monthlyAmount: response.data.monthlyAmount,
        });
        setShowPaymentConsentModal(true);
      }
    } catch (err) {
      // Handle specific error cases
      if (err.response?.data?.errorCode === "NO_PAYMENT_METHOD") {
        alert(
          "❌ No payment method found\n\nPlease add a payment method to your Stripe account before adding locations to your subscription.",
        );
      } else if (
        err.response?.data?.errorCode === "NEEDS_DEFAULT_PAYMENT_METHOD"
      ) {
        // This case is now handled by requiresConsent above, but keep as fallback
        alert("Please confirm setting your payment method as default.");
      } else {
        alert(
          err.response?.data?.message ||
            "Failed to add location to subscription. Please try again or contact support.",
        );
      }
    }
  };

  const handleAddAnother = () => {
    setFormData({
      type: [],
      locationMode: "hybrid",
      street: "",
      city: "",
      state: "",
      zipCode: "",
      phone: "",
      email: "",
      // Not detected or pre-filled - the owner must choose it for the new location.
      timezone: "",
      lat: "",
      lng: "",
    });
    setAddSuccess(false);
    setError("");
  };

  const formatBusinessType = (str) => {
    if (!str) return "";
    // Convert snake_case to Title Case (hair_salon -> Hair Salon)
    return str
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formatLocationMode = (mode) => {
    if (!mode) return "";
    return mode
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div style={{ padding: "20px" }}>Loading locations...</div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="dashboard-content">
        <div
          style={{
            display: "flex",
            flexDirection: window.innerWidth <= 768 ? "column" : "row",
            gap: window.innerWidth <= 768 ? "16px" : "0",
            justifyContent: "space-between",
            alignItems: window.innerWidth <= 768 ? "stretch" : "center",
            marginBottom: "24px",
          }}
        >
          <div>
            <h2
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: "24px",
                fontWeight: "700",
                margin: "0 0 8px 0",
              }}
            >
              Location Management
            </h2>
            <p
              style={{
                color: "#6b7280",
                margin: 0,
                fontFamily: "Inter, sans-serif",
                fontSize: "14px",
              }}
            >
              Manage all your business locations
            </p>
          </div>
          <button
            className="btn-primary"
            style={{
              padding: "8px 16px", // Reduced for smaller button
              fontFamily: "Inter, sans-serif",
              fontSize: "13px", // Slightly smaller
              fontWeight: "600",
              whiteSpace: "nowrap",
              width: window.innerWidth <= 768 ? "100%" : "auto",
              maxWidth: window.innerWidth <= 768 ? "100%" : "140px", // Prevent stretching on desktop
            }}
            onClick={() => {
              setShowAddModal(true);
              setAddSuccess(false);
              setError("");
            }}
          >
            + Add Location
          </button>
        </div>

        {/* Phase 3: Business Selector */}
        {safeBusinesses.length > 0 && (
          <BusinessSelector
            businesses={safeBusinesses}
            selectedBusinessId={selectedBusinessId}
            onBusinessChange={setSelectedBusinessId}
          />
        )}

        {/* Phase 3: Filter locations by selected business */}
        {(() => {
          const filteredLocations = selectedBusinessId
            ? locations.filter((loc) => loc.businessId === selectedBusinessId)
            : locations;

          return filteredLocations.length === 0 ? (
            <div
              className="dashboard-card"
              style={{ textAlign: "center", padding: "40px" }}
            >
              <p style={{ color: "#6b7280", marginBottom: "16px" }}>
                {selectedBusinessId
                  ? "No locations found for this business"
                  : "No locations found"}
              </p>
              <button
                className="btn-primary"
                onClick={() => {
                  setShowAddModal(true);
                  setAddSuccess(false);
                  setError("");
                }}
              >
                + Add Your First Location
              </button>
            </div>
          ) : (
            <div className="locations-grid">
              {filteredLocations.map((location) => (
                <div
                  key={location._id}
                  className="dashboard-card"
                  style={{
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Approved status at top */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "10px",
                      gap: "8px",
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "18px",
                        fontWeight: "600",
                        color: "#1a202c",
                      }}
                    >
                      {location.name}
                    </h3>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        alignItems: "flex-end",
                      }}
                    >
                      <span
                        style={{
                          padding: "3px 10px",
                          borderRadius: "12px",
                          background: location.approved ? "#ecfdf5" : "#fef3c7",
                          color: location.approved ? "#059669" : "#92400e",
                          fontSize: "11px",
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {location.approved ? "✓ Approved" : "⏳ Pending"}
                      </span>

                      {/* Subscription Status Badge */}
                      <span
                        style={{
                          padding: "3px 10px",
                          borderRadius: "12px",
                          background: location.linkedToBusinessSubscription
                            ? "#dbeafe"
                            : location.isLive && location.trialStartDate
                              ? location.trialEndDate &&
                                new Date(location.trialEndDate) < new Date()
                                ? "#fef3c7"
                                : "#ecfdf5"
                              : "#e0e7ff",
                          color: location.linkedToBusinessSubscription
                            ? "#1e40af"
                            : location.isLive && location.trialStartDate
                              ? location.trialEndDate &&
                                new Date(location.trialEndDate) < new Date()
                                ? "#92400e"
                                : "#059669"
                              : "#4338ca",
                          fontSize: "11px",
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {location.linkedToBusinessSubscription
                          ? "💳 Subscribed"
                          : location.isLive && location.trialStartDate
                            ? location.trialEndDate &&
                              new Date(location.trialEndDate) < new Date()
                              ? "⏳ Payment Pending"
                              : "✨ Trial Active"
                            : "📅 Go-Live Scheduled"}
                      </span>
                    </div>
                  </div>

                  <p
                    style={{
                      margin: "0 0 10px 0",
                      color: "#6b7280",
                      fontSize: "13px",
                    }}
                  >
                    {Array.isArray(location.type)
                      ? location.type.map(formatBusinessType).join(", ")
                      : formatBusinessType(location.type)}
                  </p>

                  {isOwnerRole && location.locationMode && (
                    <div style={{ marginBottom: "10px" }}>
                      <p
                        style={{
                          fontSize: "11px",
                          color: "#9ca3af",
                          marginBottom: "2px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          fontWeight: "600",
                        }}
                      >
                        Location Mode
                      </p>
                      <p
                        style={{
                          fontSize: "13px",
                          margin: 0,
                          color: "#374151",
                        }}
                      >
                        {formatLocationMode(location.locationMode)}
                      </p>
                    </div>
                  )}

                  {/* Compact info grid */}
                  <div style={{ marginBottom: "10px" }}>
                    <p
                      style={{
                        fontSize: "11px",
                        color: "#9ca3af",
                        marginBottom: "2px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontWeight: "600",
                      }}
                    >
                      Address
                    </p>
                    <p
                      style={{
                        fontSize: "13px",
                        margin: 0,
                        lineHeight: "1.4",
                        color: "#374151",
                      }}
                    >
                      {location.street}
                      <br />
                      {location.city}, {location.state} {location.zip}
                    </p>
                  </div>

                  {/* Phone and Email in same row if both exist */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        location.phone && location.email ? "1fr 1fr" : "1fr",
                      gap: "10px",
                      marginBottom: "10px",
                    }}
                  >
                    {location.phone && (
                      <div>
                        <p
                          style={{
                            fontSize: "11px",
                            color: "#9ca3af",
                            marginBottom: "2px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontWeight: "600",
                          }}
                        >
                          Phone
                        </p>
                        <p
                          style={{
                            fontSize: "13px",
                            margin: 0,
                            color: "#374151",
                          }}
                        >
                          {location.phone}
                        </p>
                      </div>
                    )}
                    {location.email && (
                      <div>
                        <p
                          style={{
                            fontSize: "11px",
                            color: "#9ca3af",
                            marginBottom: "2px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontWeight: "600",
                          }}
                        >
                          Email
                        </p>
                        <p
                          style={{
                            fontSize: "13px",
                            margin: 0,
                            color: "#374151",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {location.email}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Timezone and Buttons in compact row */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      paddingTop: "10px",
                      borderTop: "1px solid #e5e7eb",
                      flexWrap: "wrap",
                    }}
                  >
                    {location.timezone && (
                      <div style={{ flex: "0 0 auto", marginRight: "auto" }}>
                        <p
                          style={{
                            fontSize: "11px",
                            color: "#9ca3af",
                            marginBottom: "2px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontWeight: "600",
                          }}
                        >
                          Timezone
                        </p>
                        <p
                          style={{
                            fontSize: "12px",
                            margin: 0,
                            color: "#374151",
                          }}
                        >
                          {location.timezone}
                        </p>
                      </div>
                    )}
                    <button
                      className="btn-secondary"
                      style={{
                        padding: "6px 12px",
                        fontSize: "12px",
                        whiteSpace: "nowrap",
                        flex: "0 0 auto",
                      }}
                      onClick={() =>
                        navigate(
                          `/dashboard/locations/${location._id}/timezone`,
                        )
                      }
                    >
                      ⏰ Timezone
                    </button>

                    {/* Payment Button (only show when trial has started) */}
                    {!location.linkedToBusinessSubscription &&
                      location.isLive &&
                      location.trialStartDate && (
                        <button
                          className="btn-primary"
                          style={{
                            padding: "6px 12px",
                            fontSize: "12px",
                            whiteSpace: "nowrap",
                            flex: "0 0 auto",
                            background: "#f59e0b",
                            color: "white",
                            border: "none",
                            fontWeight: "600",
                          }}
                          onClick={() =>
                            handleAddLocationToSubscription(location._id)
                          }
                        >
                          💳 Add to Subscription
                        </button>
                      )}

                    {/* Visibility Toggle Button (only for paid locations) */}
                    {location.linkedToBusinessSubscription && (
                      <button
                        className="btn-secondary"
                        style={{
                          padding: "6px 12px",
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                          flex: "0 0 auto",
                          background: location.isPubliclyVisible
                            ? "#10b981"
                            : "#f59e0b",
                          color: "white",
                          border: "none",
                        }}
                        onClick={() =>
                          handleToggleVisibility(
                            location._id,
                            location.isPubliclyVisible,
                          )
                        }
                      >
                        {location.isPubliclyVisible
                          ? "👁️ Public"
                          : "🔒 Private"}
                      </button>
                    )}

                    <button
                      className="btn-secondary"
                      style={{
                        padding: "6px 12px",
                        fontSize: "12px",
                        whiteSpace: "nowrap",
                        flex: "0 0 auto",
                      }}
                      onClick={() =>
                        navigate(`/dashboard/shop/${location._id}`)
                      }
                    >
                      📋 Details
                    </button>
                  </div>

                  {/* Shop Visibility Status - Compact */}
                  <ShopVisibilityStatus shop={location} compact={true} />
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Add Location Modal */}
      {showAddModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
            overflow: "auto",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "16px",
              boxShadow: "0 12px 42px rgba(0,0,0,0.18)",
              padding: window.innerWidth <= 768 ? "20px" : "32px 28px",
              maxWidth: 550,
              width: "100%",
              textAlign: "left",
              position: "relative",
              maxHeight: "90vh",
              overflow: "auto",
              fontFamily: "Inter, sans-serif",
              boxSizing: "border-box",
            }}
          >
            {!addSuccess ? (
              <>
                <h4
                  style={{
                    color: "#001d4a",
                    fontWeight: 700,
                    marginBottom: 18,
                    fontFamily: "Inter, sans-serif",
                    fontSize: "20px",
                  }}
                >
                  Add Location
                </h4>
                <form
                  onSubmit={handleAddLocation}
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                >
                  <div
                    style={{
                      padding: "10px 12px",
                      borderRadius: "8px",
                      background: "#f3f4f6",
                      border: "1px solid #e5e7eb",
                      fontSize: "13px",
                      color: "#374151",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    Location name will use your business name: <strong>{getCurrentBusinessName() || "Selected Business"}</strong>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Business Type (select all that apply)
                    </label>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(200px, 1fr))",
                        gap: "12px",
                        padding: "12px",
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        background: "#f9f9f9",
                      }}
                    >
                      {businessTypes.map((type) => (
                        <label
                          key={type.value}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            value={type.value}
                            checked={formData.type.includes(type.value)}
                            onChange={handleTypeChange}
                            style={{ marginRight: "8px" }}
                          />
                          {type.emoji} {type.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Location Mode
                    </label>
                    <select
                      name="locationMode"
                      value={formData.locationMode}
                      onChange={handleChange}
                      required
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                        boxSizing: "border-box",
                      }}
                    >
                      <option value="hybrid">Hybrid</option>
                      <option value="commission_only">Commission Only</option>
                      <option value="booth_rental_only">Booth Rental Only</option>
                    </select>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Street Address
                    </label>
                    <AddressAutocompleteInput
                      value={formData.street}
                      onChange={handleStreetChange}
                      onAddressSelect={handleAddressSelect}
                      placeholder="123 Main St"
                      required
                      inputStyle={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr 1fr",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontWeight: "600",
                          fontSize: "14px",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        City
                      </label>
                      <input
                        name="city"
                        type="text"
                        placeholder="City"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        style={{
                          width: "100%",
                          padding: "10px",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                          fontSize: "14px",
                          fontFamily: "Inter, sans-serif",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontWeight: "600",
                          fontSize: "14px",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        State
                      </label>
                      <input
                        name="state"
                        type="text"
                        placeholder="IL"
                        value={formData.state}
                        onChange={handleChange}
                        required
                        style={{
                          width: "100%",
                          padding: "10px",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                          fontSize: "14px",
                          fontFamily: "Inter, sans-serif",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontWeight: "600",
                          fontSize: "14px",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        ZIP
                      </label>
                      <input
                        name="zipCode"
                        type="text"
                        placeholder="60601"
                        value={formData.zipCode}
                        onChange={handleChange}
                        required
                        style={{
                          width: "100%",
                          padding: "10px",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                          fontSize: "14px",
                          fontFamily: "Inter, sans-serif",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Phone Number
                    </label>
                    <input
                      name="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Location Email
                    </label>
                    <input
                      name="email"
                      type="email"
                      placeholder="Optional (defaults to business email)"
                      value={formData.email}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                        boxSizing: "border-box",
                      }}
                    />
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        margin: "4px 0 0 0",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Leave blank to use business email address
                    </p>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Location Timezone
                    </label>
                    <select
                      name="timezone"
                      value={formData.timezone}
                      onChange={handleChange}
                      required
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                        boxSizing: "border-box",
                        backgroundColor: "white",
                      }}
                    >
                      <option value="">Select your timezone...</option>
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </select>
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        margin: "4px 0 0 0",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Choose the timezone for this location.
                    </p>
                  </div>
                  {error && (
                    <div
                      style={{
                        color: "#d32f2f",
                        marginTop: 8,
                        fontFamily: "Inter, sans-serif",
                        fontSize: "14px",
                      }}
                    >
                      {error}
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      marginTop: 18,
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      className="btn-secondary"
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      style={{
                        minWidth: 120,
                        fontFamily: "Inter, sans-serif",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn-primary"
                      type="submit"
                      style={{
                        minWidth: 120,
                        fontFamily: "Inter, sans-serif",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Add Location
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <h4
                  style={{
                    color: "#00796b",
                    fontWeight: 700,
                    marginBottom: 18,
                    fontFamily: "Inter, sans-serif",
                    fontSize: "20px",
                  }}
                >
                  Location Added Successfully!
                </h4>
                <p
                  style={{
                    fontSize: "16px",
                    marginBottom: 18,
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  Would you like to add another location?
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    marginTop: 10,
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      setShowAddModal(false);
                      setAddSuccess(false);
                    }}
                    style={{
                      minWidth: 120,
                      fontFamily: "Inter, sans-serif",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    No, Close
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleAddAnother}
                    style={{
                      minWidth: 120,
                      fontFamily: "Inter, sans-serif",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    Yes, Add Another
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Proration Confirmation Modal */}
      {showProrationModal && prorationData && newLocationData && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "500px",
              width: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              fontFamily: "Inter, sans-serif",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>📍</div>
              <h3
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  margin: "0 0 12px",
                  color: "#111827",
                }}
              >
                Add {newLocationData.name} to Subscription?
              </h3>
              <p
                style={{
                  fontSize: "14px",
                  color: "#6b7280",
                  margin: "0 0 8px",
                }}
              >
                This location will be added to your subscription with prorated
                billing
              </p>
              <p
                style={{
                  fontSize: "12px",
                  color: "#059669",
                  background: "#d1fae5",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  margin: 0,
                }}
              >
                ✓ Location will remain private until payment is confirmed
              </p>
            </div>

            <div
              style={{
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                padding: "20px",
                marginBottom: "24px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                }}
              >
                <span style={{ fontSize: "14px", color: "#6b7280" }}>
                  Monthly Price:
                </span>
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#111827",
                  }}
                >
                  ${prorationData.monthlyPrice}/mo
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                }}
              >
                <span style={{ fontSize: "14px", color: "#6b7280" }}>
                  Billing Period:
                </span>
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#111827",
                  }}
                >
                  {prorationData.daysRemaining}{" "}
                  {prorationData.daysRemaining === 1 ? "day" : "days"} remaining
                </span>
              </div>

              <div
                style={{
                  borderTop: "1px solid #e5e7eb",
                  paddingTop: "12px",
                  marginTop: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "#111827",
                    }}
                  >
                    ⚡ Immediate Charge:
                  </span>
                  <span
                    style={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "#3b82f6",
                    }}
                  >
                    ${prorationData.immediateCharge.toFixed(2)}
                  </span>
                </div>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    margin: "0 0 12px",
                  }}
                >
                  Prorated for {prorationData.daysRemaining} days remaining in
                  current period
                </p>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span style={{ fontSize: "14px", color: "#6b7280" }}>
                    📅 New Monthly Total:
                  </span>
                  <span
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "#111827",
                    }}
                  >
                    ${prorationData.newMonthlyTotal}/mo
                  </span>
                </div>
              </div>
            </div>

            <p
              style={{
                fontSize: "13px",
                color: "#6b7280",
                textAlign: "center",
                margin: "0 0 24px",
                lineHeight: "1.6",
              }}
            >
              Starting{" "}
              {new Date(prorationData.nextBillingDate).toLocaleDateString()},
              you'll be charged the full monthly amount
            </p>

            {error && (
              <div
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "8px",
                  padding: "12px",
                  marginBottom: "16px",
                  color: "#dc2626",
                  fontSize: "14px",
                }}
              >
                {error}
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: "12px",
              }}
            >
              <button
                onClick={handleCancelProration}
                disabled={prorationLoading}
                style={{
                  flex: 1,
                  padding: "14px",
                  background: "#f3f4f6",
                  color: "#374151",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: prorationLoading ? "not-allowed" : "pointer",
                  opacity: prorationLoading ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmProration}
                disabled={prorationLoading}
                style={{
                  flex: 1,
                  padding: "14px",
                  background: prorationLoading ? "#9ca3af" : "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: prorationLoading ? "not-allowed" : "pointer",
                }}
              >
                {prorationLoading
                  ? "Processing..."
                  : `Confirm & Pay $${prorationData.immediateCharge.toFixed(2)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Subscription Confirmation Modal */}
      {showAddToSubscriptionModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: "20px",
          }}
          onClick={() => {
            setShowAddToSubscriptionModal(false);
            setPendingLocationId(null);
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "480px",
              width: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              fontFamily: "Inter, sans-serif",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>💳</div>
              <h3
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  margin: "0 0 12px",
                  color: "#111827",
                }}
              >
                Add Location to Subscription?
              </h3>
              <p
                style={{
                  fontSize: "15px",
                  color: "#6b7280",
                  lineHeight: "1.6",
                  margin: 0,
                }}
              >
                You'll be charged a prorated amount for the current billing
                period, then the full monthly rate going forward.
              </p>
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                marginTop: "24px",
              }}
            >
              <button
                onClick={() => {
                  setShowAddToSubscriptionModal(false);
                  setPendingLocationId(null);
                }}
                style={{
                  flex: 1,
                  padding: "12px 24px",
                  fontSize: "15px",
                  fontWeight: "600",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  backgroundColor: "white",
                  color: "#374151",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#f9fafb";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "white";
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowAddToSubscriptionModal(false);
                  handleAddLocationToSubscription(pendingLocationId, true);
                }}
                style={{
                  flex: 1,
                  padding: "12px 24px",
                  fontSize: "15px",
                  fontWeight: "600",
                  border: "none",
                  borderRadius: "8px",
                  backgroundColor: "#10b981",
                  color: "white",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#059669";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#10b981";
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Method Consent Modal */}
      {showPaymentConsentModal && paymentConsentData && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: "20px",
          }}
          onClick={() => {
            setShowPaymentConsentModal(false);
            setPaymentConsentData(null);
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "520px",
              width: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              fontFamily: "Inter, sans-serif",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>💳</div>
              <h3
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  margin: "0 0 12px",
                  color: "#111827",
                }}
              >
                Confirm Payment Method
              </h3>
              <p
                style={{
                  fontSize: "14px",
                  color: "#6b7280",
                  margin: 0,
                }}
              >
                We found your{" "}
                {paymentConsentData.paymentMethod.brand.toUpperCase()} card on
                file
              </p>
            </div>

            {/* Card Details */}
            <div
              style={{
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                padding: "20px",
                marginBottom: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    fontSize: "32px",
                    marginRight: "12px",
                  }}
                >
                  💳
                </div>
                <div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#111827",
                    }}
                  >
                    {paymentConsentData.paymentMethod.brand.toUpperCase()} ••••{" "}
                    {paymentConsentData.paymentMethod.last4}
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#6b7280",
                    }}
                  >
                    Expires {paymentConsentData.paymentMethod.expMonth}/
                    {paymentConsentData.paymentMethod.expYear}
                  </div>
                </div>
              </div>

              <div
                style={{
                  borderTop: "1px solid #e5e7eb",
                  paddingTop: "16px",
                }}
              >
                <div style={{ marginBottom: "12px" }}>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#6b7280",
                      marginBottom: "4px",
                    }}
                  >
                    To proceed, we'll:
                  </div>
                  <ul
                    style={{
                      margin: 0,
                      paddingLeft: "20px",
                      fontSize: "14px",
                      color: "#374151",
                      lineHeight: "1.8",
                    }}
                  >
                    <li>Set this card as your default payment method</li>
                    <li>
                      {paymentConsentData.prorationAmount &&
                      !isNaN(paymentConsentData.prorationAmount) &&
                      isFinite(paymentConsentData.prorationAmount)
                        ? `Charge $${paymentConsentData.prorationAmount.toFixed(2)} now (prorated for current period)`
                        : "Charge a prorated amount for the current period"}
                    </li>
                    <li>
                      Add ${paymentConsentData.monthlyAmount.toFixed(2)}/month
                      to future invoices
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
              }}
            >
              <button
                onClick={() => {
                  setShowPaymentConsentModal(false);
                  setPaymentConsentData(null);
                }}
                style={{
                  flex: 1,
                  padding: "12px 24px",
                  fontSize: "15px",
                  fontWeight: "600",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  backgroundColor: "white",
                  color: "#374151",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#f9fafb";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "white";
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowPaymentConsentModal(false);
                  handleAddLocationToSubscription(
                    paymentConsentData.locationId,
                    true,
                  );
                }}
                style={{
                  flex: 1,
                  padding: "12px 24px",
                  fontSize: "15px",
                  fontWeight: "600",
                  border: "none",
                  borderRadius: "8px",
                  backgroundColor: "#10b981",
                  color: "white",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#059669";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#10b981";
                }}
              >
                Confirm & Charge Card
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Location Created But Not Activated Modal */}
      {showLocationCreatedModal && locationCreatedData && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: "20px",
          }}
          onClick={() => {
            setShowLocationCreatedModal(false);
            setLocationCreatedData(null);
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "520px",
              width: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              fontFamily: "Inter, sans-serif",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>📍</div>
              <h3
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  margin: "0 0 12px",
                  color: "#111827",
                }}
              >
                Location "{locationCreatedData.name}" was created but not
                activated
              </h3>
              <p
                style={{
                  fontSize: "15px",
                  color: "#6b7280",
                  lineHeight: "1.6",
                  margin: 0,
                }}
              >
                It will remain <strong>PRIVATE</strong> and{" "}
                <strong>NOT visible to customers</strong> until you:
              </p>
            </div>

            <div
              style={{
                background: "#fef3c7",
                border: "1px solid #fbbf24",
                borderRadius: "12px",
                padding: "20px",
                marginBottom: "24px",
              }}
            >
              <ol
                style={{
                  margin: 0,
                  paddingLeft: "20px",
                  fontSize: "15px",
                  color: "#78350f",
                  lineHeight: "1.8",
                }}
              >
                <li>
                  Add it to your subscription by clicking the action button
                </li>
                <li>Complete payment</li>
              </ol>
            </div>

            <div
              style={{
                background: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: "8px",
                padding: "12px",
                marginBottom: "24px",
                fontSize: "13px",
                color: "#166534",
                textAlign: "center",
              }}
            >
              💡 You can delete it if you change your mind.
            </div>

            <button
              onClick={() => {
                setShowLocationCreatedModal(false);
                setLocationCreatedData(null);
              }}
              style={{
                width: "100%",
                padding: "12px 24px",
                fontSize: "15px",
                fontWeight: "600",
                border: "none",
                borderRadius: "8px",
                backgroundColor: "#10b981",
                color: "white",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#059669";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#10b981";
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Location Added to Subscription Success Modal */}
      {showLocationAddedModal && locationAddedData && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: "20px",
          }}
          onClick={() => {
            setShowLocationAddedModal(false);
            setLocationAddedData(null);
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "480px",
              width: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              fontFamily: "Inter, sans-serif",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <div style={{ fontSize: "64px", marginBottom: "16px" }}>✅</div>
              <h3
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  margin: "0 0 12px",
                  color: "#059669",
                }}
              >
                Location added to subscription!
              </h3>
              <p
                style={{
                  fontSize: "15px",
                  color: "#6b7280",
                  lineHeight: "1.6",
                  margin: 0,
                }}
              >
                Your next invoice will include this location.
              </p>
            </div>

            {locationAddedData.prorationAmount && (
              <div
                style={{
                  background: "#f0fdf4",
                  border: "1px solid #86efac",
                  borderRadius: "12px",
                  padding: "16px",
                  marginBottom: "24px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "13px",
                    color: "#166534",
                    marginBottom: "4px",
                  }}
                >
                  Prorated charge
                </div>
                <div
                  style={{
                    fontSize: "28px",
                    fontWeight: "700",
                    color: "#059669",
                  }}
                >
                  ${locationAddedData.prorationAmount.toFixed(2)}
                </div>
              </div>
            )}

            <button
              onClick={() => {
                setShowLocationAddedModal(false);
                setLocationAddedData(null);
              }}
              style={{
                width: "100%",
                padding: "12px 24px",
                fontSize: "15px",
                fontWeight: "600",
                border: "none",
                borderRadius: "8px",
                backgroundColor: "#10b981",
                color: "white",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#059669";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#10b981";
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </SidebarLayout>
  );
};

export default Locations;
